import { AlertData, AlertType, AlertTypeContextData } from '../../../types/alerts/AlertTypes';
import { isInhibitorQuestionnaireCompleted } from '../../../utils/cargo-temp-log/cargoTempLogUtils';
import { useVesselStore } from '../../../context/VesselContext';
import { useStowageStore } from '../../../context/StowageContext';
import { useCargoTempLogStore } from '../../../context/CargoTempLogContext';
import { getFormattedCargoNumber } from 'src/utils/stowage/cargoOperationUtils';

/**
 * Custom hook to check for missing inhibitor questionnaire on inhibited cargoes during SOSP events
 */
export const useCheckInhibitorQuestionnaire = () => {
    const { vessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { cargoTemperatureConfigs } = useCargoTempLogStore();

    const checkInhibitorQuestionnaire = async (
        contextData: AlertTypeContextData[AlertType.MISSING_INHIBITOR_QUESTIONNAIRE]
    ): Promise<AlertData[]> => {
        const alerts: AlertData[] = [];
        const { portCallId, alertSourceDate } = contextData;

        // Find cargoes loaded during the port call
        const berthCalls = loadedMasterStowagePlan.berthCalls.filter((call) => call.portCall.imosPortCallId === portCallId);

        const loadingOperations = berthCalls.flatMap((call) => call.cargoOperations.filter((op) => op.operationType === 'Loading'));

        const inhibitedCargoes = loadingOperations.flatMap((op) => op.cargo).filter((cargo) => cargo.product.category.isInhibited);

        if (inhibitedCargoes.length === 0) {
            return alerts; // No inhibited cargoes, no need to check
        }

        // Check each inhibited cargo for complete inhibitor questionnaire
        for (const cargo of inhibitedCargoes) {
            const cargoConfig = cargoTemperatureConfigs?.find((config) => config.cargoNumber === cargo.cargoNumber.toString());

            if (!cargoConfig || !isInhibitorQuestionnaireCompleted(cargoConfig)) {
                alerts.push({
                    alertTypeId: AlertType.MISSING_INHIBITOR_QUESTIONNAIRE,
                    alertSource: 'OrcaWeb Port log',
                    alertType: 'Missing Inhibitor Questionnaire',
                    vesselName: vessel.vesselName || 'Unknown',
                    imoNumber: vessel.imoNumber || 0,
                    value: 1,
                    limit: 0,
                    highSeverity: true,
                    cargoNumber: getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, false),
                    alertSourceDate: alertSourceDate ? new Date(alertSourceDate) : new Date(),
                });
            }
        }
        return alerts;
    };

    return { checkInhibitorQuestionnaire };
};
